import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Alert,
  Paper,
  Stack,
  Button,
  Chip,
  TextField,
  InputAdornment,
  Avatar,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PeopleIcon from '@mui/icons-material/People';
import SearchIcon from '@mui/icons-material/Search';
import Header from '../../components/Layout/Header';
import Sidebar from '../../components/Layout/Sidebar';
import { classService, studentService } from '../../services/apiService';
import { useParams, useNavigate } from 'react-router-dom';
import { formatDate } from '../../utils/dateFormat';
import { getAvatarColor, getInitials } from '../../utils/avatar';

const ClassStudents = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { classId } = useParams();

  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  const drawerWidth = 280;

  const [classInfo, setClassInfo] = useState(null);
  const [students, setStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadData = useCallback(async () => {
    if (!classId) return;
    setLoading(true);
    setError(null);
    try {
      const [classesRes, studentsRes] = await Promise.all([
        classService.getAll(),
        studentService.getAll({ class_id: classId, status: 'active' }),
      ]);

      const classesList = Array.isArray(classesRes?.data) ? classesRes.data : (Array.isArray(classesRes) ? classesRes : []);
      const info = classesList.find((cls) => cls.id === classId);
      setClassInfo(info || null);

      const studentList = Array.isArray(studentsRes?.data) ? studentsRes.data : (Array.isArray(studentsRes) ? studentsRes : []);
      const sorted = [...studentList].sort((a, b) => {
        const rollA = parseInt(a.roll_number, 10);
        const rollB = parseInt(b.roll_number, 10);
        if (!isNaN(rollA) && !isNaN(rollB)) return rollA - rollB;
        return (a.roll_number || '').localeCompare(b.roll_number || '');
      });
      setStudents(sorted);
      setFilteredStudents(sorted);
    } catch (err) {
      setError(err.message || 'Failed to load class details');
    } finally {
      setLoading(false);
    }
  }, [classId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const getClassLabel = useCallback(() => {
    if (!classInfo) return '—';
    const base = classInfo.name?.toString() || '';
    const name = base.startsWith('Class ') ? base : (base ? `Class ${base}` : '');
    return classInfo.section ? `${name} - ${classInfo.section}` : name || '—';
  }, [classInfo]);


  const handleSearch = (event) => {
    const value = event.target.value;
    setSearchTerm(value);
    if (!value.trim()) {
      setFilteredStudents(students);
      return;
    }
    const lower = value.toLowerCase();
    const filtered = students.filter((student) =>
      (student.full_name || '').toLowerCase().includes(lower)
    );
    setFilteredStudents(filtered);
  };

  return (
    <>
      <Header onMenuClick={() => setSidebarOpen(true)} />
      <Box sx={{ display: 'flex' }}>
        <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} drawerWidth={drawerWidth} />
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 0,
            width: { md: `calc(100% - ${drawerWidth}px)` },
            ml: { md: `${0}px` },
            mt: '64px',
            minHeight: 'calc(100vh - 64px)',
            backgroundColor: 'background.default',
          }}
        >
          <Box sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <Paper
                  elevation={0}
                  sx={{
                    mb: 3,
                    p: { xs: 2, sm: 2.5, md: 3 },
                    borderRadius: 2,
                    border: 1,
                    borderColor: 'divider',
                    background: theme.palette.background.paper,
                  }}
                >
                  <Stack spacing={2}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" flexWrap="wrap">
                      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
                        <Button
                          startIcon={<ArrowBackIcon />}
                          onClick={() => navigate(-1)}
                          variant="outlined"
                          size="small"
                          sx={{
                            fontWeight: 600,
                            borderWidth: 1.5,
                            '&:hover': {
                              borderWidth: 1.5,
                              transform: 'translateY(-1px)',
                              boxShadow: 2,
                            },
                          }}
                        >
                          Back
                        </Button>
                        <Typography variant="h6" fontWeight={700} sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.375rem' } }}>
                          Class: {getClassLabel()}
                        </Typography>
                      </Stack>
                      <Chip
                        icon={<PeopleIcon />}
                        label={`${students.length} students`}
                        size="small"
                        color="primary"
                        variant="filled"
                        sx={{ fontWeight: 600 }}
                      />
                    </Stack>
                    <Typography
                      variant="body2"
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 1,
                        color: 'text.secondary',
                        alignItems: 'center',
                      }}
                    >
                      <strong>Head Teacher:</strong> {classInfo?.head_teacher_name || 'Not assigned'}
                      <span>·</span>
                      {classInfo?.head_teacher_email || '—'}
                      <span>·</span>
                      {classInfo?.head_teacher_phone || '—'}
                    </Typography>
                  </Stack>
                </Paper>

                <Box sx={{ mb: 3 }}>
                  <TextField
                    fullWidth
                    placeholder="Search students by name..."
                    value={searchTerm}
                    onChange={handleSearch}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon color="action" fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>

                {filteredStudents.length === 0 ? (
                  <Paper variant="outlined" sx={{ p: 3, borderRadius: 3, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      No students found in this class.
                    </Typography>
                  </Paper>
                ) : (
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: '1fr', // single card per row
                      gap: { xs: 1.5, sm: 2 },
                    }}
                  >
                    {filteredStudents.map((row) => {
                      const isActive = row.is_active === '1' || row.is_active === 1 || row.is_active === true;
                      return (
                        <Paper
                          key={row.id}
                          variant="outlined"
                          sx={{
                            p: { xs: 2, sm: 2.4 },
                            borderRadius: 2,
                            borderColor: 'divider'
                          }}
                        >
                          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'flex-start' }} justifyContent="space-between" flexWrap="nowrap">
                            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
                              <Avatar
                                src={row.profile_image || null}
                                alt={row.full_name}
                                sx={{
                                  width: 48,
                                  height: 48,
                                  bgcolor: getAvatarColor(row.full_name),
                                  color: '#ffffff',
                                  fontWeight: 700,
                                }}
                              >
                                {!row.profile_image && getInitials(row.full_name)}
                              </Avatar>
                              <Box sx={{ flex: 1, minWidth: 200 }}>
                                <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
                                  <Typography variant="subtitle1" fontWeight={700}>
                                    {row.full_name}
                                  </Typography>
                                  <Chip
                                    size="small"
                                    label={isActive ? 'Active' : 'Inactive'}
                                    color={isActive ? 'success' : 'default'}
                                    variant="outlined"
                                  />
                                </Stack>
                                <Typography variant="body2" color="text.secondary">
                                  <strong>Roll:</strong> {row.roll_number ?? '—'} &nbsp;·&nbsp;
                                  <strong>Class:</strong> {getClassLabel()}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  <strong>DOB:</strong> {formatDate(row.date_of_birth)} &nbsp;·&nbsp;
                                  <strong>Admission:</strong> {formatDate(row.admission_date)}
                                </Typography>
                              </Box>
                            </Stack>
                            {/* Middle: parent details - hidden on mobile */}
                            <Stack spacing={0.5} sx={{ flex: { xs: '1 1 100%', md: '0 0 280px' }, minWidth: { xs: '100%', md: 240 }, display: { xs: 'none', md: 'flex' } }}>
                              <Typography variant="body2" color="text.secondary">
                                <strong>Parent:</strong> {row.parent_name || '—'}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                <strong>Contact:</strong> {row.parent_phone || '—'}
                              </Typography>
                              {row.parent_email ? (
                                <Typography variant="body2" color="text.secondary">
                                  <strong>Email:</strong> {row.parent_email}
                                </Typography>
                              ) : null}
                            </Stack>
                            {/* Right: actions */}
                            <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end" sx={{ flex: { xs: '1 1 100%', md: '0 0 220px' }, minWidth: { xs: '100%', md: 180 }, flexWrap: 'wrap', rowGap: 1 }}>
                              {/* No View/Edit buttons for class students view */}
                            </Stack>
                          </Stack>
                        </Paper>
                      );
                    })}
                  </Box>
                )}
              </>
            )}
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default ClassStudents;

